/**
 * La extensión `.js` es obligatoria aquí por lo mismo que en `dimensiones.ts`:
 * este archivo lo compila también la configuración del servidor —lo arrastran
 * las pruebas de `tests/web/`—, que resuelve al estilo de Node y la exige.
 */
import type { Cuestionario, Grupo, Pregunta } from './api.js';
import { tituloDeGrupo } from './titulos.js';

/**
 * Poner el resultado que ya se conoce, sin responder el test.
 *
 * ESTÁ SEPARADO DE LA PANTALLA A PROPÓSITO, igual que `dimensiones.ts`: aquí no
 * hay JSX, así que las reglas con criterio —qué se puede pedir, dónde arranca
 * cada control y qué salto admite— se pueden probar. Dentro del componente no.
 *
 * QUIÉN DECIDE SI ESTO SE OFRECE: el servidor, con `admiteResultadosConocidos`.
 * Saber si una parte se resume en un número exige saber qué esquema mide, y eso
 * es información clínica que no tiene por qué bajar al navegador solo para
 * pintar un botón. Aquí se da por hecho lo que él contestó.
 */

type Escala = Extract<Pregunta, { tipo: 'escala' }>;

/** Un control en la pantalla: una parte del test resumida en un número. */
export interface ParteParaResultado {
  grupoId: string;
  titulo: string;
  minimo: number;
  maximo: number;
  /**
   * Cuánto se mueve el control de un paso al siguiente.
   *
   * NO ES UN VALOR DE ADORNO: el resultado de una parte es el promedio de sus
   * ítems, así que con cinco ítems enteros solo puede caer en múltiplos de 0,2.
   * Un control que dejara elegir 4,37 enseñaría un número que el mapa después no
   * puede dar, y quien lo viera pensaría que se le movió algo.
   */
  paso: number;
  /** Dónde arranca: el centro de la escala, ajustado a un paso alcanzable. */
  inicial: number;
  /** Lo que dice el extremo bajo de la escala, si la psicóloga lo escribió. */
  extremoBajo: string | null;
  extremoAlto: string | null;
}

function escalasDe(grupo: Grupo): Escala[] {
  return grupo.preguntas.filter((p): p is Escala => p.tipo === 'escala');
}

/** El texto de un valor concreto de la escala, o `null` si no lleva ninguno. */
function etiquetaDe(item: Escala, valor: number): string | null {
  const texto = item.etiquetas.find((e) => e.valor === valor)?.texto.trim() ?? '';
  return texto === '' ? null : texto;
}

/** Ajusta un valor al múltiplo del paso más cercano, contando desde el mínimo. */
export function ajustarAlPaso(valor: number, minimo: number, paso: number): number {
  if (paso <= 0) return valor;
  const pasos = Math.round((valor - minimo) / paso);
  // Se redondea a tres decimales porque `minimo + pasos * 0.2` en binario da
  // 3.4000000000000004, y ese número acabaría escrito en pantalla.
  return Math.round((minimo + pasos * paso) * 1000) / 1000;
}

/**
 * Los controles que hay que pintar, uno por parte del test.
 *
 * Devuelve la lista vacía si alguna parte no tiene ítems de escala: en ese caso
 * no hay ningún número que pedir y la pantalla no tiene sentido. No debería
 * pasar —el servidor ya dijo que sí—, pero aquí no se supone nada de él.
 */
export function partesParaResultados(cuestionario: Cuestionario): ParteParaResultado[] {
  const partes: ParteParaResultado[] = [];

  for (const grupo of cuestionario.grupos) {
    const items = escalasDe(grupo);
    const primero = items[0];
    if (!primero) return [];

    const paso = 1 / items.length;
    const centro = ajustarAlPaso((primero.minimo + primero.maximo) / 2, primero.minimo, paso);

    partes.push({
      grupoId: grupo.id,
      titulo: tituloDeGrupo(grupo),
      minimo: primero.minimo,
      maximo: primero.maximo,
      paso,
      inicial: centro,
      extremoBajo: etiquetaDe(primero, primero.minimo),
      extremoAlto: etiquetaDe(primero, primero.maximo),
    });
  }

  return partes;
}

/** Dónde arranca cada control antes de que nadie lo toque. */
export function valoresIniciales(partes: readonly ParteParaResultado[]): Record<string, number> {
  return Object.fromEntries(partes.map((p) => [p.grupoId, p.inicial]));
}

/**
 * El número como se lee en pantalla, con coma decimal.
 *
 * SIEMPRE CON UN DECIMAL, incluso el 4 entero, que se lee «4,0». Con la coma
 * puesta o quitada según el valor, la columna de números baila al mover
 * cualquier control y cuesta comparar dos partes de un vistazo.
 */
export function comoSeLee(valor: number): string {
  return valor.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
